import { Column, Row } from "@silevis/reactgrid";

export type LegalColumnIds = {
  chapter?: string;
  section?: string;
  article?: string;
  paragraph?: string;
  item?: string;
  content?: string;
};

export const getLegalColumnIds = (columns: Column[]): LegalColumnIds => {
  const byLabel = new Map<string, string>();
  columns.forEach((col: any) => {
    if (typeof col?.label === "string" && col.columnId) {
      byLabel.set(col.label.trim(), String(col.columnId));
    }
  });

  return {
    chapter: byLabel.get("章"),
    article: byLabel.get("条"),
    paragraph: byLabel.get("項"),
    item: byLabel.get("号"),
    content: byLabel.get("条文"),
  };
};

const ARTICLE_CONTENT_PATTERN = /第[0-9０-９]+条/;

export const buildCompanyPoliciesChanges = (
  rows: Row<any>[],
  columns: Column[],
  ids: LegalColumnIds,
) => {
  const { chapter, section, article, paragraph, item, content } = ids;
  if (!chapter && !section && !article && !paragraph && !item) return [];

  const levelOrder = [
    { key: "chapter", columnId: chapter },
    { key: "article", columnId: article },
    { key: "paragraph", columnId: paragraph },
    { key: "item", columnId: item },
  ] as const;

  const counters = {
    chapter: 0,
    article: 0,
    paragraph: 0,
    item: 0,
  };

  const changes: any[] = [];

  const getCell = (row: any, columnId?: string) => {
    if (!columnId) return null;
    return row.cells.find((c: any) => c.columnId === columnId);
  };

  const getText = (cell: any) => {
    return typeof cell?.text === "string" ? cell.text.trim() : "";
  };

  const toHalfWidthDigits = (s: string) =>
    s.replace(/[０-９]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
    );

  const toFullWidthDigits = (s: string) =>
    s.replace(/[0-9]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0xfee0));

  const getNumber = (text: string) => {
    const match = toHalfWidthDigits(text).match(/\d+/);
    return match ? `${Number(match[0])}` : "";
  };

  const originalArticles: {
    rowId: any;
    oldText: string;
    idx: number;
    newValue?: string;
  }[] = [];
  const originalContents: { rowId: any; text: string }[] = [];

  rows.forEach((row: any, index: number) => {
    if (row.rowId === "header") return;

    if (article) {
      const articleText = getText(getCell(row, article));
      originalArticles.push({
        rowId: row.rowId,
        oldText: getNumber(articleText),
        idx: index,
      });
    }

    if (content) {
      const contentText = getText(getCell(row, content));
      if (ARTICLE_CONTENT_PATTERN.test(contentText)) {
        originalContents.push({ rowId: row.rowId, text: contentText });
      }
    }
  });
  const originalArticlesHasFormatContent = originalArticles.filter(
    (article) => {
      if (!article.oldText) return false;

      const num = toHalfWidthDigits(article.oldText).match(/\d+/)?.[0];
      if (!num) return false;

      const regex = new RegExp(`第${num}条`);

      return originalContents.some((content) => {
        const text = toHalfWidthDigits(content.text);
        return regex.test(text);
      });
    },
  );

  const setCellText = (row: any, columnId?: string, value?: string) => {
    if (!columnId) return;
    const cell = getCell(row, columnId);
    if (!cell || cell.nonEditable) return;

    const nextText = value ?? "";
    if ((cell.text ?? "") === nextText) return;

    changes.push({
      rowId: row.rowId,
      columnId: cell.columnId,
      type: cell.type,
      previousCell: cell,
      newCell: { ...cell, text: nextText },
    });
  };

  const clearLowerLevels = (startIndex: number, row: any) => {
    for (let i = startIndex; i < levelOrder.length; i += 1) {
      setCellText(row, levelOrder[i].columnId, "");
    }
  };

  rows.forEach((row: any) => {
    if (row.rowId === "header") return;

    // 🔥 detect tất cả level có value
    const hasValue = {
      chapter: false,
      section: false,
      article: false,
      paragraph: false,
      item: false,
    };

    for (const level of levelOrder) {
      if (!level.columnId) continue;
      const cell = getCell(row, level.columnId);
      const text = getText(cell);
      if (text) {
        hasValue[level.key] = true;
      }
    }

    if (hasValue.chapter) {
      counters.chapter += 1;
      counters.paragraph = 0;
      counters.item = 0;

      setCellText(row, chapter, String(counters.chapter));
      clearLowerLevels(1, row);
      return;
    }

    if (hasValue.article) {
      counters.article += 1;
      counters.paragraph = 1;
      counters.item = 0;
      const newValue = String(counters.article);
      const entry = originalArticles.find((a) => a.rowId === row.rowId);
      if (entry) entry.newValue = newValue;
      setCellText(row, paragraph, String(counters.paragraph));
      setCellText(row, article, newValue);
      setCellText(row, item, "");

      return;
    }

    if (hasValue.paragraph) {
      counters.paragraph += 1;
      counters.item = 0;

      setCellText(row, paragraph, String(counters.paragraph));
      setCellText(row, item, "");
      return;
    }

    // ===== ITEM =====
    if (hasValue.item) {
      counters.item += 1;
      setCellText(row, item, String(counters.item));
    }
  });

  if (content) {
    originalContents.forEach(({ rowId, text }) => {
      let didChange = false;
      const nextText = text.replace(
        /第([0-9０-９]+)条/g,
        (match, num: string) => {
          const normalized = toHalfWidthDigits(num);
          const entry = originalArticlesHasFormatContent?.find((a) => {
            return toHalfWidthDigits(a.oldText) === normalized;
          });

          if (entry?.newValue !== undefined) {
            didChange = true;
            const isFullWidth = /[０-９]/.test(num);
            const newNum = isFullWidth
              ? toFullWidthDigits(entry.newValue)
              : entry.newValue;
            return `第${newNum}条`;
          }
          return match;
        },
      );

      if (didChange) {
        const row = rows.find((r: any) => r.rowId === rowId);
        if (row) setCellText(row, content, nextText);
      }
    });
  }

  return changes;
};
